import { useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XIcon } from 'lucide-react';
import { BlockFields } from './BlockFields';
import { blockKinds, blockLabel, nextBlockId, newBlock } from './newsletterRules';
import { move } from '../editing';
import { btnIcon } from '../ui/styles';
import type { NewsletterBlock, NewsletterBlockKind } from '../../core/types/newsletter';
type Props = {
  blocks: NewsletterBlock[];
  onChange: (blocks: NewsletterBlock[]) => void;
};

/** De inhoud van de mail: blokken van boven naar beneden, te verplaatsen en weg te halen. */
export function NewsletterBlocks({
  blocks,
  onChange
}: Props) {
  const [adding, setAdding] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);
  const update = (i: number, block: NewsletterBlock) => onChange(blocks.map((b, j) => j === i ? block : b));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const add = (kind: NewsletterBlockKind) => {
    const block = newBlock(kind, nextBlockId(blocks));
    onChange([...blocks, block]);
    setAdding(false);
    // Het nieuwe blok in beeld, met de focus op het eerste veld: op de telefoon staat het anders onder de vouw.
    requestAnimationFrame(() => {
      const el = listRef.current?.querySelector<HTMLElement>(`[data-block="${block.id}"]`);
      el?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
      el?.querySelector<HTMLElement>('input, select, textarea')?.focus({
        preventScroll: true
      });
    });
  };
  return <div data-id="element-3212" data-mp="2okxs6f11361j">
      {blocks.length === 0 ? <p className="rounded-md border border-dashed border-line px-4 py-6 text-center text-[13px] text-muted" data-id="element-3213" data-mp="2ojw51c6qmt3g">De mail is nog leeg. Voeg hieronder een blok toe.</p> : <ol ref={listRef} className="space-y-4" data-id="element-3214" data-mp="2fioitshhxipq">
          {blocks.map((b, i) => <li key={b.id} data-block={b.id} className="rounded-md border border-line bg-shell/60" data-id="element-3215" data-mp="z584c87iezl9" data-mpi={i}>
              <div className="flex items-center gap-1 border-b border-line px-3 py-1.5 sm:px-4" data-id="element-3216" data-mp="144ugsffikbt0">
                <span className="min-w-0 flex-1 text-[13px] font-medium text-ink" data-id="element-3217" data-mp="k2pva12w42s9">
                  <span className="mr-1.5 tabular-nums text-muted" data-id="element-3218" data-mp="g14p0ykv2mr1">{i + 1}.</span>
                  {blockLabel(b.kind)}
                </span>
                <button type="button" onClick={() => onChange(move(blocks, i, i - 1))} disabled={i === 0} aria-label={`Blok ${i + 1} omhoog`} className={btnIcon} data-id="element-3219" data-mp="b8l9hw2m5hjl">
                  <ArrowUpIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-3220" data-mp="1chxlyhym4jg" />
                </button>
                <button type="button" onClick={() => onChange(move(blocks, i, i + 1))} disabled={i === blocks.length - 1} aria-label={`Blok ${i + 1} omlaag`} className={btnIcon} data-id="element-3221" data-mp="3shrsa3gwf6ze">
                  <ArrowDownIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-3222" data-mp="1mp1h537xetvw" />
                </button>
                <button type="button" onClick={() => remove(i)} aria-label={`Blok ${i + 1} weghalen`} className={btnIcon} data-id="element-3223" data-mp="1hq8bty2sm96t">
                  <XIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-3224" data-mp="1paiym2fsf6gr" />
                </button>
              </div>
              <div className="p-3 sm:p-4" data-id="element-3225" data-mp="1u7sn9fvcre3j">
                <BlockFields block={b} onChange={next => update(i, next)} data-id="element-3226" data-mp="1u6rclci90tmc" />
              </div>
            </li>)}
        </ol>}

      <div className="mt-4" data-id="element-3227" data-mp="23yx28fl12yyd">
        {adding ? <div role="group" aria-label="Soort blok" className="rounded-md border border-line p-2" data-id="element-3228" data-mp="240qc03qxtzal">
            <ul className="grid gap-1 sm:grid-cols-2" data-id="element-3229" data-mp="16czlammar1oj">
              {blockKinds.map(k => <li key={k.kind} data-id="element-3230" data-mp="1bc2k3pboobo2">
                  <button type="button" onClick={() => add(k.kind)} className="flex min-h-[44px] w-full flex-col rounded-md px-3 py-2 text-left transition-colors duration-150 ease-soft hover:bg-sand" data-id="element-3231" data-mp="1g8jj5ekf8afx">
                    <span className="text-[14px] text-ink" data-id="element-3232" data-mp="2vptbl67vqlgw">{k.label}</span>
                    <span className="text-[12px] leading-snug text-muted" data-id="element-3233" data-mp="2vrwdaniea31g">{k.text}</span>
                  </button>
                </li>)}
            </ul>
            <button type="button" onClick={() => setAdding(false)} className="mt-1 w-full rounded-md px-3 py-2 text-[13px] text-muted hover:bg-sand hover:text-ink" data-id="element-3234" data-mp="2pamf1aqckxov">
              Niet toevoegen
            </button>
          </div> : <button type="button" onClick={() => setAdding(true)} className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md border border-dashed border-line text-[14px] text-ink transition-colors duration-150 ease-soft hover:border-ink/40" data-id="element-3235" data-mp="2za3kbr1ot9kb">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3236" data-mp="3hyicihnqqtmp" />
            Blok toevoegen
          </button>}
      </div>
    </div>;
}